import React, { useState, useEffect } from 'react';
import { FiKey, FiTrash2, FiPlus, FiSmartphone, FiMonitor, FiShield, FiClock } from 'react-icons/fi';
import api from '../../services/api';
import toast from 'react-hot-toast';
import PasskeyRegistration from './PasskeyRegistration';
import { useAuth } from '../../contexts/AuthContext';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

interface Credential {
  id: string;
  nickname?: string;
  transports?: string[];
  createdAt?: string;
  lastUsedAt?: string;
}

const PasskeyManager: React.FC = () => {
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddPasskey, setShowAddPasskey] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async () => {
    try {
      const response = await api.getWebAuthnCredentials();
      setCredentials(response.data.credentials || []);
    } catch (error) {
      console.error('Failed to load credentials:', error);
      toast.error('Failed to load passkeys');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCredential = async (credentialId: string) => {
    if (!confirm('Are you sure you want to delete this passkey? You won\'t be able to use it to sign in anymore.')) {
      return;
    }

    setDeletingId(credentialId);
    try {
      await api.deleteWebAuthnCredential(credentialId);
      toast.success('Passkey deleted successfully');
      setCredentials(prev => prev.filter(c => c.id !== credentialId));
    } catch (error) {
      console.error('Failed to delete credential:', error);
      toast.error('Failed to delete passkey');
    } finally {
      setDeletingId(null);
    }
  };

  const handlePasskeyAdded = () => {
    setShowAddPasskey(false);
    loadCredentials();
    toast.success('Passkey added successfully!');
  };

  const getDeviceIcon = (transports?: string[]) => {
    if (!transports || transports.length === 0) {
      return <FiKey className="w-5 h-5" />;
    }
    
    if (transports.includes('internal')) {
      return <FiSmartphone className="w-5 h-5" />;
    } else if (transports.includes('usb') || transports.includes('nfc')) {
      return <FiShield className="w-5 h-5" />;
    } else {
      return <FiMonitor className="w-5 h-5" />;
    }
  };

  const getDeviceType = (transports?: string[]) => {
    if (!transports || transports.length === 0) {
      return 'Security Key';
    }
    
    if (transports.includes('internal')) {
      return 'This Device';
    } else if (transports.includes('usb')) {
      return 'USB Security Key';
    } else if (transports.includes('nfc')) {
      return 'NFC Security Key';
    } else if (transports.includes('ble')) {
      return 'Bluetooth Device';
    } else {
      return 'External Authenticator';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-16 bg-gray-100 rounded"></div>
            <div className="h-16 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Passkeys</h3>
          <p className="text-sm text-gray-600 mt-1">
            Manage your passkeys for passwordless sign-in
          </p>
        </div>
        {!showAddPasskey && (
          <button
            onClick={() => setShowAddPasskey(true)}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            <FiPlus className="w-4 h-4 mr-2" />
            Add Passkey
          </button>
        )}
      </div>

      {showAddPasskey && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <PasskeyRegistration
            userId={user?.id}
            userEmail={user?.email || ''}
            onSuccess={handlePasskeyAdded}
            onSkip={() => setShowAddPasskey(false)}
          />
        </div>
      )}

      {credentials.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FiKey className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">No passkeys yet</h4>
          <p className="text-sm text-gray-600 mb-6 max-w-sm mx-auto">
            Add a passkey to enable passwordless sign-in with Face ID, Touch ID, Windows Hello, or security keys
          </p>
          {!showAddPasskey && (
            <button
              onClick={() => setShowAddPasskey(true)}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <FiPlus className="w-4 h-4 mr-2" />
              Add Your First Passkey
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {credentials.map((credential) => (
            <div
              key={credential.id}
              className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-1">
                  {getDeviceIcon(credential.transports)}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">
                    {credential.nickname || getDeviceType(credential.transports)}
                  </p>
                  <div className="flex items-center space-x-4 mt-1">
                    <p className="text-xs text-gray-500">
                      Type: {getDeviceType(credential.transports)}
                    </p>
                    {credential.lastUsedAt && (
                      <p className="text-xs text-gray-500 flex items-center">
                        <FiClock className="w-3 h-3 mr-1" />
                        Last used {dayjs(credential.lastUsedAt).fromNow()}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => handleDeleteCredential(credential.id)}
                disabled={deletingId === credential.id}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deletingId === credential.id ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600" />
                ) : (
                  <FiTrash2 className="w-5 h-5" />
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {credentials.length > 0 && (
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <div className="flex">
            <FiShield className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm text-blue-900 font-medium">Security tip</p>
              <p className="text-sm text-blue-700 mt-1">
                Having multiple passkeys ensures you can always access your account, even if you lose a device or security key.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PasskeyManager;